import { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import Image from 'components/Common/Image'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { isHotelPackage } from 'lib/offer/offerTypes'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import CartPanelPickedForYouPill from './CartPanelPickedForYouPill'
import useCartPanelHotelOfferData, { type CartPanelHotelOffer } from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.three};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(10)} ${rem(12)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-self: flex-start;
  width: fit-content;
  min-width: min(${rem(300)}, 100%);
  max-width: 100%;
  container-type: inline-size;
  container-name: room-card;
`

const RoomCardRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(360)}) {
    flex-basis: 100%;
    align-self: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const CheckoutDetailsBanner = styled(MessageBanner)`
  > div {
    padding-top: ${rem(8)};
    padding-bottom: ${rem(8)};
  }

  a,
  button {
    --color-primary-bg: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --color-primary-hover: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --color-primary-active: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-hover-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
    --button-hover-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-hover-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-active-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
    --button-active-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-active-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-focus-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-focus-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  }
`

function getRoomImage(pkg: App.Package | App.BedbankPackage | undefined): App.Image | undefined {
  if (!pkg) return undefined
  if ('roomType' in pkg) return pkg.roomType?.images?.[0]
  if ('images' in pkg) return pkg.images[0]
  return undefined
}

function getCancellationPolicyType(pkg: App.Package | App.BedbankPackage | undefined) {
  if (!pkg || !('roomRate' in pkg)) return undefined
  return pkg.roomRate?.cancellationPolicy.type
}

function isHotelOffer(offer: CartPanelHotelOffer | undefined): offer is App.HotelOffer {
  return !!offer && offer.type !== 'bedbank_hotel'
}

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onSetDates: () => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelNeedsDatesTile({
  item,
  onToggleInclude,
  onSetDates: _onSetDates,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { offer, pkg, checkIn } = useCartPanelHotelOfferData(item)
  const eventBus = useTripPlannerEventBus()

  const handleCardClick = useCallback(() => {
    if (!item.offerId) return
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.offerId, scrollToRoomOptions: true })
  }, [item.offerId, eventBus])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const roomImage = getRoomImage(pkg) ?? (item.roomImageId ? { id: item.roomImageId } : undefined)
  const cancellationPolicyType = getCancellationPolicyType(pkg)

  const loyaltyCalculationRequest = useMemo(() => {
    if (!isHotelOffer(offer) || !item.pricing.price) return undefined
    return generateLoyaltyPointsCalculatorHotelOptions(offer, {
      pkg: isHotelPackage(pkg) ? pkg : undefined,
      price: item.pricing.price,
      memberPrice: item.pricing.memberPrice,
      currentRegionCode,
      nights: item.duration,
    })
  }, [offer, pkg, item.pricing.price, item.pricing.memberPrice, item.duration, currentRegionCode])

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {item.duration && (
        <BodyText variant="medium" colour="neutral-three">
          {intl.formatMessage(
            {
              id: 'GQY0DKErdsrlLFeRV9Ax',
              defaultMessage: '{nights}-night stay',
              description: 'Night count shown under a hotel name when dates are missing but the trip duration is known',
            },
            { nights: item.duration },
          )}
        </BodyText>
      )}
    </Group>
  )

  const addDatesToCheckoutLabel = intl.formatMessage({
    id: 'l37kymVhZxcxOCLV15q6',
    defaultMessage: 'Add dates to include in checkout',
    description: 'Warning banner prompting the user to add dates so the hotel can be included in checkout',
  })

  const addDatesLabel = intl.formatMessage({
    id: '5NZpwNagm7j-mUPX1BLo',
    defaultMessage: 'Add dates',
    description: 'Action button that opens the hotel drawer so the user can add dates to the hotel',
  })

  const hasRoom = !!(item.roomName || item.occupancy)
  const shouldShowPickedForYou = item.packageAutoSelected && item.pricing.isAvailable !== false

  const topBanner = (
    <CheckoutDetailsBanner
      kind="warning"
      density="compact"
      primaryActionButtonProps={{
        children: addDatesLabel,
        onClick: handleCardClick,
        disabled: isStreaming || !item.offerId,
        size: 'small',
      }}
    >
      {addDatesToCheckoutLabel}
    </CheckoutDetailsBanner>
  )

  const roomCard = hasRoom ? (
    <RoomCard>
      <RoomCardRow>
        {roomImage && (
          <RoomImageWrapper>
            <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
          </RoomImageWrapper>
        )}
        <RoomCardContent>
          {shouldShowPickedForYou && <CartPanelPickedForYouPill />}
          {item.roomName && <BodyText variant="medium" weight="bold">{item.roomName}</BodyText>}
          {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
        </RoomCardContent>
        <ChangeRoomButtonWrapper>
          <TextButton kind="tertiary" size="small" startIcon={<LinePenIcon />} onClick={handleCardClick} disabled={isStreaming || !item.offerId}>
            {intl.formatMessage({
              id: 'BrfTYdJonj0A3hETLQR8',
              defaultMessage: 'Change room',
              description: 'Link to change the selected room for a hotel in the cart panel',
            })}
          </TextButton>
        </ChangeRoomButtonWrapper>
      </RoomCardRow>
    </RoomCard>
  ) : undefined

  const extraHotelDetails = (
    <Group direction="vertical" gap={8}>
      {isHotelOffer(offer) && cancellationPolicyType && (
        <TileCancellationPolicy
          cancellationPolicyType={cancellationPolicyType}
          checkInDate={checkIn ?? ''}
          timezoneOffset={offer.property.timezoneOffset || 0}
          offerType={offer.type}
          textSize="medium"
        />
      )}
      {loyaltyCalculationRequest && (
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={[loyaltyCalculationRequest]}
          calculationType="exact"
          offer={isHotelOffer(offer) ? offer : undefined}
        />
      )}
    </Group>
  )

  const attention = (
    <Group direction="vertical" gap={8}>
      {extraHotelDetails}
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price ? (
    <CartReviewPriceStack
      price={item.pricing.price}
      memberPrice={item.pricing.memberPrice}
      value={item.pricing.value ?? 0}
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      onCardClick={item.offerId ? handleCardClick : undefined}
      topBanner={topBanner}
      header={header}
      roomCard={roomCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelNeedsDatesTile
